/** @jsxImportSource @emotion/react */
import React from "react";
import { Space, Button, Tooltip, Typography } from "antd";
import { DataTable, Toolbox } from "../../DataTable";
import { ColumnConfig } from "../../DataTable/types";
import { ConfigOption } from "../../../usecases/HomeConfig/types";
import { HomeConfigUtil } from "../../../usecases/HomeConfig/utils";
import * as Styles from "./styles";

interface OptionTableProps {
  options: ConfigOption[];
  editingOptionName?: string;
  onEditOption: (index: number) => void;
  onDeleteOption: (index: number) => void;
  onReorderOption: (from: number, to: number) => void;
}

export const OptionTable = ({
  options,
  editingOptionName,
  onEditOption,
  onDeleteOption,
  onReorderOption,
}: OptionTableProps) => {
  const [keyword, setKeyword] = React.useState<string>("");

  const normalizedKeyword = keyword.trim().toLowerCase();
  // NOTE: the base config's map node is edited via its own drawer variant, so
  // it must never appear as a pickable option here.
  const pickableOptions = options.filter((option) => {
    return HomeConfigUtil.isBaseOption(option) === false;
  });
  const filteredOptions = normalizedKeyword
    ? pickableOptions.filter((option) => {
        const name = option.name.toLowerCase();
        const group = (option.customGroup ?? "").toLowerCase();
        return (
          name.includes(normalizedKeyword) || group.includes(normalizedKeyword)
        );
      })
    : pickableOptions;

  const handleSearch = (value: string) => {
    setKeyword(value);
  };

  const renderNodesCell = (_: unknown, record: ConfigOption) => {
    const text = JSON.stringify(record.nodes);
    return (
      <Tooltip title={text}>
        <Typography.Paragraph
          ellipsis={{ rows: 2 }}
          copyable={{ text }}
          css={Styles.ellipsisCellStyle}
        >
          {text}
        </Typography.Paragraph>
      </Tooltip>
    );
  };

  const renderOptionActions = (_: unknown, record: ConfigOption) => {
    const realIndex = options.findIndex((option) => {
      return option.name === record.name;
    });
    const handleEdit = () => {
      onEditOption(realIndex);
    };
    const handleDelete = () => {
      onDeleteOption(realIndex);
    };
    return (
      <Space>
        <Button size="small" onClick={handleEdit}>
          Edit
        </Button>
        <Button size="small" danger onClick={handleDelete}>
          Delete
        </Button>
      </Space>
    );
  };

  const columnConfigs: ColumnConfig<ConfigOption>[] = [
    {
      key: "name",
      title: "Option Name",
      type: "text",
    },
    {
      key: "customGroup",
      title: "Group",
      type: "plain",
      render: (value) => (value as string) || "—",
    },
    {
      key: "nodes",
      title: "Nodes",
      type: "plain",
      render: (_value, record) => renderNodesCell(undefined, record),
    },
    {
      key: "actions",
      title: "Actions",
      type: "plain",
      width: 140,
      render: (_value, record) => renderOptionActions(undefined, record),
    },
  ];

  const getRowClassName = (record: ConfigOption) => {
    return record.name === editingOptionName ? "ant-table-row-selected" : "";
  };

  const canReorder = normalizedKeyword === "";

  return (
    <Space direction="vertical" css={Styles.optionTableStyle}>
      <Toolbox>
        <Toolbox.Search
          size="middle"
          placeholder="Find..."
          onSearch={handleSearch}
        />
      </Toolbox>
      <DataTable<ConfigOption>
        rowKey="name"
        dataSource={filteredOptions}
        columnConfigs={columnConfigs}
        pagination
        rowDnd={canReorder}
        onRowReorder={onReorderOption}
        rowClassName={getRowClassName}
      />
    </Space>
  );
};
